import React, { useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { Link } from 'react-router-dom';
import { 
  FaRobot, 
  FaMicrophoneAlt, 
  FaArrowRight, 
  FaShoppingBag, 
  FaMobileAlt, 
  FaCheckCircle,
  FaTimes,
  FaChartLine,
  FaBolt,
  FaStore
} from 'react-icons/fa';

import 'swiper/css';
import 'swiper/css/navigation';

// ============================================================================
// 1. Distinct Mobile & Web Applications (10 Unique Apps & SaaS MVPs)
// ============================================================================
export const uniqueAppPortfolio = [
  {
    id: 'profitiq',
    name: 'ProfitIQ',
    category: 'Fintech & SaaS',
    tagline: 'Financial Portfolio & AI Market Forecasting',
    description: 'AI-driven investment analytics platform with automated portfolio rebalancing, real-time stock/crypto feeds, and predictive trend forecasting.',
    image: '/portfolio/apps/profitiq_showcase_1600x1200_4x3.png',
    stack: ['Flutter', 'Node.js', 'FastAPI', 'PostgreSQL', 'Stripe'],
    metrics: ['+44% User Retention', '<50ms Query Latency', '$20M+ Managed Vol'],
    highlights: [
      'Real-time WebSocket market ticker feed with zero jitter',
      'Proprietary AI predictive asset allocation model',
      'Multi-currency biometric wallet security',
      'Automated tax-loss harvesting reporting'
    ],
    featured: true
  },
  {
    id: 'edusaaspro',
    name: 'EduSaaS Pro',
    category: 'Fintech & SaaS',
    tagline: 'Multi-Tenant Cloud Learning & Academy Management SaaS',
    description: 'Enterprise LMS platform with drag-and-drop course curriculum builder, student progress tracking, live video streaming, and automated Stripe billing tiers.',
    image: '/portfolio/saas/edusaaspro_showcase_2400x2400.png',
    stack: ['React 18', 'Next.js 15', 'Node.js', 'PostgreSQL', 'Stripe Billing'],
    metrics: ['120k+ Enrolled Students', '99.9% Uptime SLA', '+62% Academy MRR'],
    highlights: [
      'Multi-tenant data isolation and custom branded subdomains',
      'Interactive video lesson player with automated timestamped notes',
      'Granular instructor revenue split payouts via Stripe Connect',
      'Automated quiz grading and verifiable PDF certificate generation'
    ],
    featured: true
  },
  {
    id: 'socialgenie',
    name: 'SocialGenie SaaS',
    category: 'Fintech & SaaS',
    tagline: 'AI Social Media Management & Omni-Channel Scheduling Platform',
    description: 'SaaS command center enabling marketing agencies to generate viral copy, schedule posts across 7 social networks, and track live engagement analytics.',
    image: '/portfolio/saas/socialgenie_showcase_2400x2400.png',
    stack: ['React', 'TypeScript', 'Express', 'Redis Queue', 'Meta Graph API'],
    metrics: ['+320% Social Reach', '50k+ Posts Scheduled/mo', '<0.8s Dashboard Load'],
    highlights: [
      'Unified drag-and-drop multi-channel content calendar',
      'AI caption generator fine-tuned on top-performing viral hooks',
      'Direct API publishing to LinkedIn, Instagram, TikTok, and X',
      'Real-time engagement heatmaps and audience demographics'
    ],
    featured: true
  },
  {
    id: 'oizvehadar',
    name: 'OizVeHadar Digital Library SaaS',
    category: 'Fintech & SaaS',
    tagline: 'Multi-Tenant Digital Torah Archive, Volume Indexer & Multi-Device Cloud Reader',
    description: 'Enterprise digital publication and archival SaaS platform featuring 100,000+ indexed classic volumes, high-performance bi-directional Hebrew OCR search, cloud reading synchronization, offline caching, and tiered subscription access.',
    image: '/portfolio/saas/oizvehadar_library_showcase_2400x2400.png',
    stack: ['React 18', 'Node.js', 'FastAPI', 'Elasticsearch', 'AWS CloudFront', 'IndexedDB'],
    metrics: ['100k+ Seforim Volumes', '<80ms Search Latency', '50k+ Active Readers'],
    highlights: [
      'Comprehensive Oz VeHadar catalog classification with hierarchical edition indexing',
      'High-performance bi-directional Hebrew text rendering with multi-device reading sync',
      'Offline library caching and desktop/mobile synchronization via IndexedDB',
      'Global multi-region CDN asset delivery with sub-second page rendering'
    ],
    featured: true
  },
  {
    id: 'ryderz',
    name: 'Ryderz',
    category: 'On-Demand & Mobility',
    tagline: 'On-Demand Mobility, Logistics & Courier App',
    description: 'Hyperlocal ride-hailing and parcel delivery platform with live GPS tracking, dynamic fare calculation, multi-stop routing, and digital wallet checkout.',
    image: '/portfolio/apps/ryderz_showcase_1600x1200_4x3.png',
    stack: ['Flutter', 'Google Maps API', 'Node.js', 'Socket.io', 'MongoDB'],
    metrics: ['<3s Driver Match', '+35% Dispatch Efficiency', '50k+ Completed Rides'],
    highlights: [
      'Real-time vector GPS car routing and ETA prediction',
      'Automated dynamic surge pricing algorithms',
      'Driver KYC onboarding & multi-stop trip routing',
      'In-app cashless digital wallet and tipping'
    ],
    featured: true
  },
  {
    id: 'stylehub',
    name: 'StyleHub',
    category: 'E-Commerce & Retail',
    tagline: 'Modern Fashion & Mobile Shopping Experience',
    description: 'High-conversion fashion commerce app featuring 1-click checkout, interactive sizing charts, personalized lookbooks, and instant order tracking.',
    image: '/portfolio/apps/stylehub_showcase_1600x1200_4x3.png',
    stack: ['Flutter', 'Shopify Storefront API', 'Stripe', 'Node.js', 'GraphQL'],
    metrics: ['+52% Mobile Conversion', '0.8s Checkout Speed', '4.9★ App Store'],
    highlights: [
      'Headless Shopify Storefront API GraphQL synchronization',
      'Interactive 3D garment visualizer and sizing tool',
      'Apple Pay / Google Pay one-tap frictionless checkout',
      'Personalized AI lookbook recommendations'
    ],
    featured: true
  },
  {
    id: 'restrogenie',
    name: 'RestroGenie SaaS',
    category: 'E-Commerce & Retail',
    tagline: 'Smart Restaurant POS, QR Table Ordering & Kitchen Display',
    description: 'All-in-one restaurant operational system with contactless QR digital menus, real-time Kitchen Display System (KDS), split-bill payments, and inventory tracking.',
    image: '/portfolio/saas/restrogeniee_showcase_1200x1200.png',
    stack: ['Next.js', 'Socket.io', 'Node.js', 'MongoDB', 'Stripe Terminal'],
    metrics: ['-40% Table Turn Time', '100% Order Accuracy', '+28% Average Bill Size'],
    highlights: [
      'Instant QR table ordering without mobile app installation',
      'Real-time kitchen ticket routing with audio bump alerts',
      'Automated ingredient-level inventory decrementing',
      'Loyalty points & automated SMS review requests'
    ],
    featured: false
  },
  {
    id: 'recipe_finder',
    name: 'Recipe Finder',
    category: 'Lifestyle & AI',
    tagline: 'Smart Culinary & AI Ingredient Meal Planner',
    description: 'Smart kitchen companion app with AI camera scanning of fridge ingredients, automated dietary filtering, and step-by-step interactive cooking guides.',
    image: '/portfolio/apps/recipe_finder_showcase_1600x1200_4x3.png',
    stack: ['Flutter', 'Vision AI', 'Spoonacular API', 'Supabase', 'Tailwind'],
    metrics: ['+80% Meal Prep Time Saved', '100k+ Recipes Scanned', '4.8★ Rating'],
    highlights: [
      'Computer vision ingredient recognition from fridge photo',
      'Automated macro nutrient and calorie calculation',
      'Step-by-step voice-guided cooking instruction mode',
      'Smart grocery shopping list export'
    ],
    featured: false
  },
  {
    id: 'fitness_tracker',
    name: 'PulseFit',
    category: 'Health & Fitness',
    tagline: 'AI Workout Coach & Daily Activity Tracker',
    description: 'Personalized fitness tracker with AI routine generation, Apple Health / Google Fit sync, workout timers, and detailed milestone analytics.',
    image: '/portfolio/apps/fitness_app_iphone15_mockup.jpg',
    stack: ['Flutter', 'HealthKit', 'Google Fit', 'Firebase', 'Node.js'],
    metrics: ['+60% Goal Completion', '100k+ Active Workouts', 'Real-Time Sync'],
    highlights: [
      'Bi-directional Apple HealthKit & Google Fit synchronization',
      'AI adaptive workout routines matching target heart rate',
      'Interactive rep counter and rest interval timer',
      'Community challenges and gamified streak leaderboards'
    ],
    featured: false
  },
  {
    id: 'lybaz',
    name: 'Lybaz',
    category: 'E-Commerce & Retail',
    tagline: 'Curated Luxury Boutique & Exclusive Apparel',
    description: 'Luxury lifestyle mobile boutique with VIP designer drops, flash sales, high-res lookbook galleries, and integrated global shipping.',
    image: '/portfolio/apps/lybaz_app_iphone15_mockup.jpg',
    stack: ['React Native', 'Shopify API', 'Stripe', 'Node.js', 'AWS CloudFront'],
    metrics: ['+48% VIP Order Value', '1.2s Global CDN Load', 'Zero Fraud Chargebacks'],
    highlights: [
      'Exclusive member-only drop countdown gates',
      'High-definition zoomable fabric detail galleries',
      'Multi-currency global tax and duties checkout',
      '3D Secure automated anti-fraud checkout protection'
    ],
    featured: false
  }
];

// ============================================================================
// 2. Distinct Custom Shopify Stores (8 Unique Storefronts, Zero Duplicates)
// ============================================================================
export const uniqueShopifyPortfolio = [
  {
    id: 'cocobeauty',
    name: 'Coco Beauty',
    category: 'Beauty & Skincare',
    tagline: 'Luxury Organic Skincare & Clean Beauty Storefront',
    description: 'Custom Shopify 2.0 theme engineering with interactive ingredient breakdowns, subscription checkout (Recharge), bundle builder, and automated review syndication.',
    image: '/portfolio/shopify/cocobeauty_showcase_1600x1200_4x3.png',
    stack: ['Shopify Liquid', 'Custom Theme 2.0', 'Recharge Subscriptions', 'Klaviyo SMS', 'Tailwind'],
    metrics: ['+52% Subscription Growth', '1.1s Page Load', '+38% Conversion Rate'],
    highlights: [
      'Interactive Clean Beauty Ingredient Transparency Matrix',
      'Tiered "Bundle & Save" Mix-and-Match Builder',
      'Recharge 1-Click Subscription Self-Management Portal',
      'Automated Yotpo Verified Customer Review Syndication'
    ],
    featured: true
  },
  {
    id: 'colderatti',
    name: 'Colderatti',
    category: 'Fashion & Apparel',
    tagline: 'High-End Streetwear & Luxury Apparel Brand',
    description: 'Ultra-fast headless Shopify storefront featuring high-res lookbooks, instant size recommendation modals, pre-order queues, and multi-currency international checkout.',
    image: '/portfolio/shopify/colderatti_showcase_1600x1200_4x3.png',
    stack: ['Hydrogen Headless', 'Shopify Plus', 'GraphQL Storefront API', 'Stripe Multi-Currency', 'Tailwind'],
    metrics: ['99/100 Mobile Speed', '+64% Mobile Conversions', '120+ Currencies'],
    highlights: [
      'Sub-second page transitions via Shopify Hydrogen Headless',
      'Interactive Streetwear Drop Countdown & Pre-Order Queue',
      'AI Smart Sizing Fit Recommendation Modal',
      'Shoppable Lookbook with Instant Add-to-Cart Hotspots'
    ],
    featured: true
  },
  {
    id: 'eightsleep',
    name: 'Eight Sleep',
    category: 'Health & Tech',
    tagline: 'High-Performance Sleep Tech & Smart Mattresses',
    description: 'Engineered high-converting direct-to-consumer store with custom 3D pod visualizers, financing calculators (Affirm/Klarna), and dynamic temperature quiz funnels.',
    image: '/portfolio/shopify/eightsleep_showcase_1600x1200_4x3.png',
    stack: ['Shopify Plus', 'Liquid & React Components', 'Affirm / Klarna API', 'PostHog Analytics', 'Tailwind'],
    metrics: ['+42% Average Order Value', '+35% Funnel Completion', '<1.3s Initial Render'],
    highlights: [
      'Dynamic 3D Sleep Pod Interactive Visualizer',
      'Diagnostic Sleep Quiz with Personalized Product Bundling',
      'Real-time Affirm & Klarna Installment Payment Schedule',
      'High-velocity post-purchase cross-sell flows'
    ],
    featured: true
  },
  {
    id: 'exposureapparel',
    name: 'Exposure Apparel',
    category: 'Fashion & Apparel',
    tagline: 'Premium Athleisure & Activewear Brand Storefront',
    description: 'Dynamic apparel eCommerce experience featuring color-swatch product filtering, live inventory badges, rapid 1-click cart drawer, and personalized cross-sells.',
    image: '/portfolio/shopify/exposureapparel_showcase_1600x1200_4x3.png',
    stack: ['Shopify 2.0', 'Custom Liquid & AJAX Cart', 'Klaviyo SMS', 'Gorgias Helpdesk', 'Tailwind'],
    metrics: ['+45% Repeat Purchases', '+28% Cart-to-Checkout', '0.9s Slide Cart Open'],
    highlights: [
      'Slide-Out AJAX Cart Drawer with Dynamic Free Shipping Meter',
      'Live Low-Stock Scarcity Counters on High-Demand Sizes',
      'Instant Color Swatch Previews on Collection Pages',
      'Omnichannel VIP Rewards and Loyalty Integration'
    ],
    featured: true
  },
  {
    id: 'kcurlsys',
    name: 'K-Curlsys',
    category: 'Hair Care & Salons',
    tagline: 'Specialist Curly Hair Systems & Salon eCommerce',
    description: 'Comprehensive salon and retail storefront with custom curl-type diagnostic quiz, professional wholesale B2B pricing portal, and automated salon replenishment.',
    image: '/portfolio/shopify/kcurlsys_showcase_1600x1200_4x3.png',
    stack: ['Shopify Liquid', 'B2B Wholesale Portal', 'Custom Quiz Funnel', 'Yotpo Reviews', 'Tailwind'],
    metrics: ['+80% B2B Reorders', '+55% Quiz-to-Cart Conversion', '10k+ Salon Members'],
    highlights: [
      'Interactive 5-Step Curl Diagnostic Routine Quiz',
      'Gated Professional Salon Wholesale B2B Pricing Tier',
      'Automated Salon Replenishment Subscription Schedules',
      'Interactive Certified Stylist & Salon Locator Map'
    ],
    featured: false
  },
  {
    id: 'leekepicks',
    name: 'Leeke Picks',
    category: 'D2C Lifestyle',
    tagline: 'Trending Curated Lifestyle, Gadgets & Direct-to-Consumer Goods',
    description: 'High-velocity D2C storefront built for social commerce traffic with sticky checkout bars, countdown timer flash drops, and video UGC reels integration.',
    image: '/portfolio/shopify/leekepicks_showcase_1600x1200_4x3.png',
    stack: ['Shopify 2.0', 'TikTok Pixel & Meta CAPI', 'Video UGC Player', 'Loox Photo Reviews', 'Tailwind'],
    metrics: ['3.2x ROAS on TikTok Ads', '+39% Impulse Purchases', '<1.0s Mobile Load'],
    highlights: [
      'TikTok-Style Shoppable Video Reels with Direct Add-to-Bag',
      'Persistent High-Conversion Sticky Add-to-Cart Bar',
      'Flash Drop Scarcity Countdown & Tiered Quantity Discounts',
      'High-Trust Loox Visual Customer Photo & Video Reviews'
    ],
    featured: false
  },
  {
    id: 'organicsnature',
    name: 'Organics Nature',
    category: 'Supplements & Wellness',
    tagline: '100% Wildcrafted Sea Moss & Superfood Health Store',
    description: 'High-trust health wellness brand store featuring subscription auto-ship discounts, lab certificate badges, custom recipe blog integration, and rapid cart upsells.',
    image: '/portfolio/shopify/organicsnature_showcase_1600x1200_4x3.png',
    stack: ['Shopify Plus', 'Recharge Subscriptions', 'Custom Liquid & Webhooks', 'Omnisend Automation', 'Tailwind'],
    metrics: ['+72% Active Subscribers', '+41% Checkout Conversion', '4.9★ Average Rating'],
    highlights: [
      'Subscribe & Save 20% Auto-Ship Engine with Flexible Frequency',
      'Third-Party Lab Purity Certificate Badges & Trust Seals',
      'Superfood Recipe Hub with 1-Click Recipe Ingredient Cart Adds',
      'Smart In-Cart Product Recommendation Upsells'
    ],
    featured: false
  },
  {
    id: 'tryauri',
    name: 'Try Auri',
    category: 'Health & Wellness',
    tagline: 'Functional Mushroom Gummies & Daily Nootropic Wellness Brand',
    description: 'Clean modern nutraceutical storefront with interactive benefit matrices, tiered quantity bundle discounts, verified buyer UGC carousels, and seamless mobile pay.',
    image: '/portfolio/shopify/tryauri_showcase_1600x1200_4x3.png',
    stack: ['Shopify 2.0', 'Tiered Bundle Builder', 'Apple Pay / Google Pay Express', 'Klaviyo Flows', 'Tailwind'],
    metrics: ['+58% Average Order Value', '+46% Mobile Checkout Rate', '<1.2s Page Speed'],
    highlights: [
      'Interactive Mushroom Benefit & Nootropic Functional Matrix',
      '"Buy 2 Get 1 Free" Tiered Bundle Builder & Quantity Selectors',
      'Express 1-Tap Apple Pay / Google Pay Mobile Checkout',
      'Verified Customer Video & Before/After Experience Carousels'
    ],
    featured: false
  }
];

// ============================================================================
// 3. AI Automation Workflows, Voice Telephony & RAG Agent Suite (Secondary)
// ============================================================================
export const uniqueAIPortfolio = [
  {
    id: 'ai-voice-calling-agent',
    name: '24/7 AI Voice Calling & Telephony Agent',
    category: 'Voice AI',
    tagline: 'Ultra-Low Latency Conversational Voice Agent for Support & Inbound Sales',
    description: 'Real-time bidirectional speech-to-speech conversational voice agent engineered with ultra-low latency (<420ms) for inbound customer support, lead qualification, dynamic calendar scheduling, and automated CRM record synchronization.',
    image: '/portfolio/ai/ai_voice_calling_agent_showcase.jpg',
    stack: ['Retell AI / Vapi', 'ElevenLabs Turbo v2', 'Twilio Voice', 'Whisper STT', 'FastAPI', 'HubSpot CRM'],
    metrics: ['<420ms Voice Latency', '99.4% Booking Rate', '10,000+ Automated Calls', '98% Positive CSAT'],
    highlights: [
      'Sub-500ms voice synthesis with dynamic human interruption handling',
      'Live real-time speech-to-text transcription stream with sentiment telemetry',
      'Bi-directional automated CRM synchronization with HubSpot, Salesforce, and Shopify',
      'Autonomous calendar booking with instant SMS/WhatsApp confirmation dispatch'
    ],
    featured: true
  },
  {
    id: 'shopify-workflow-automation',
    name: 'Shopify Store Autonomous Operations Workflow',
    category: 'E-Commerce Ops',
    tagline: 'End-to-End Autonomous Fulfillment, Anti-Fraud Scoring & Inventory Sync',
    description: 'Intelligent multi-node automation workflow connecting Shopify webhooks to warehouse logistics, automated AI fraud risk scoring, customer notification triggers, and accounting reconciliation.',
    image: '/portfolio/ai/shopify_store_workflow_automation_mockup.png',
    stack: ['Shopify Webhooks', 'n8n Workflow Engine', 'OpenAI Vision', 'Klaviyo API', 'PostgreSQL'],
    metrics: ['99.8% Faster Fulfillment', 'Zero Manual Data Entry', '$45k+ Monthly Saved'],
    highlights: [
      'Real-time webhook order routing and automatic stock balance adjust across 3 warehouses',
      'AI anti-fraud analysis evaluating address velocity and transaction risk patterns',
      'Automated customer tracking update emails & SMS via Klaviyo flows',
      'QuickBooks / Xero automated revenue sync and tax reconciliation'
    ],
    featured: true
  },
  {
    id: 'lead-generation-auto-email',
    name: 'Multi-Agent Inbound & Outbound Lead Engine',
    category: 'Growth & Leads',
    tagline: 'Autonomous Lead Enrichment, AI Personalization & Cold Outreach Sequences',
    description: 'Multi-node lead generation system that enriches corporate data via Apollo/Clearbit, drafts personalized cold emails using fine-tuned LLMs, and schedules follow-up sequences based on reply sentiment.',
    image: '/portfolio/ai/lead_generation_auto_email_mockup.png',
    stack: ['n8n / Make.com', 'GPT-4o', 'Apollo API', 'Instantly.ai', 'Google Sheets CRM'],
    metrics: ['+340% Pipeline Velocity', '4.2x Meeting Booking Rate', '15k+ Verified Leads/mo'],
    highlights: [
      'Automated firmographic data enrichment and MX-record email verification',
      'Context-aware dynamic email generation based on company news and tech stack',
      'Automated reply sentiment classifier routing positive leads directly to sales rep calendars',
      'Zero bounce rate guardrails with automated SMTP warmups'
    ],
    featured: true
  },
  {
    id: 'ai-invoice-automation',
    name: 'AI Invoice OCR & Financial Reconciliation Engine',
    category: 'Operations & ERP',
    tagline: 'Autonomous Receipt OCR, Line-Item Extraction & 1-Click Slack Approvals',
    description: 'Automated accounts payable workflow that extracts complex multi-page invoices via LLM Vision and OCR, verifies PO matching, detects discrepancies, and routes for 1-click approvals in Slack.',
    image: '/portfolio/ai/ai_invoice_automation_mockup.png',
    stack: ['OpenAI Vision', 'LangChain', 'n8n', 'Xero / QuickBooks API', 'Slack Bot API'],
    metrics: ['<15s per Invoice', '99.6% Extraction Accuracy', '85% Operational Cost Cut'],
    highlights: [
      'Multi-lingual document OCR parsing with handwritten and table item recognition',
      'Automated 3-way line-item matching against purchase orders and bank statements',
      'Interactive Slack bot with 1-click Approve / Reject / Flag action buttons',
      'Automatic PDF archive storage in AWS S3 and immutable audit logging'
    ],
    featured: false
  },
  {
    id: 'customer-support-rag-agent',
    name: 'Customer Support RAG Knowledge AI Agent',
    category: 'Support & RAG',
    tagline: 'Knowledge-Grounded Autonomous Support & Live Ticket Triage',
    description: 'Retrieval-Augmented Generation (RAG) support agent connected to internal documentation, Notion, and Zendesk tickets to instantly resolve 80%+ tier-1 support queries across web chat and WhatsApp.',
    image: '/portfolio/ai/customer_support_rag_agent_mockup.png',
    stack: ['Pinecone Vector DB', 'OpenAI Embeddings', 'LangChain', 'Zendesk API', 'WhatsApp API'],
    metrics: ['82% First-Contact Resolution', '24/7 Zero Wait Time', '4.9/5 CSAT Score'],
    highlights: [
      'Hybrid semantic vector search with real-time documentation syncing',
      'Multi-turn conversational memory with seamless human handover fallback',
      'Multi-channel deployment across web widget, WhatsApp, and Slack',
      'Automatic sentiment analysis and escalation triggers for high-value accounts'
    ],
    featured: false
  },
  {
    id: 'social-caption-generator-publisher',
    name: 'Social Media Content Gen & Omni-Channel Publisher',
    category: 'Content & Social',
    tagline: 'Autonomous Social Copywriting, Carousel Generation & Scheduling',
    description: 'End-to-end creative pipeline that turns raw ideas, URLs, or product launches into tailored captions, hashtags, and visual carousels published simultaneously to LinkedIn, X, Instagram, and TikTok.',
    image: '/portfolio/ai/social_caption_generator_publisher_mockup.png',
    stack: ['Claude 3.5 Sonnet', 'n8n', 'Buffer / Hootsuite API', 'Canva API', 'Airtable'],
    metrics: ['10x Content Output', '35+ Posts/Week Automated', '+180% Organic Impressions'],
    highlights: [
      'Multi-platform voice tone adaptation (B2B LinkedIn vs viral TikTok)',
      'Automated trending hashtag scraping and engagement analytics logging',
      'Visual slide carousel generation from blog post summaries',
      'Airtable editorial calendar review with 1-click publishing webhooks'
    ],
    featured: false
  },
  {
    id: 'social-messaging-order-mgmt',
    name: 'Social Messaging & Direct Order Automation',
    category: 'E-Commerce Ops',
    tagline: 'Instagram DM & WhatsApp Automated Conversational Commerce',
    description: 'Conversational checkout bot that handles customer product inquiries over Instagram DMs and WhatsApp, checks live inventory, and generates direct Stripe/Shopify payment links.',
    image: '/portfolio/ai/social_messaging_order_mgmt_mockup.png',
    stack: ['Meta Graph API', 'WhatsApp Business API', 'Shopify Storefront API', 'Stripe', 'n8n'],
    metrics: ['+65% Direct DM Sales', '<5s Instant Response', '24/7 Automated Orders'],
    highlights: [
      'Natural language product recommendation engine inside direct messages',
      'Dynamic Stripe checkout link generation with applied discount codes',
      'Instant shipping tracking lookups via carrier API integrations',
      'Abandoned conversation remarketing triggers'
    ],
    featured: false
  },
  {
    id: 'appointment-booking-ai',
    name: 'Smart AI Appointment Booking & Calendar Agent',
    category: 'Operations & ERP',
    tagline: 'Conversational Calendar Booking, Rescheduling & CRM Qualification',
    description: 'Autonomous scheduling agent that engages leads over web chat, WhatsApp, or SMS, qualifies budget/timeline requirements, checks Google/Outlook calendar availability in real-time, and schedules meetings.',
    image: '/portfolio/ai/appointment_booking_ai_mockup.png',
    stack: ['Google Calendar API', 'Outlook API', 'Twilio SMS', 'Make.com', 'OpenAI Assistant'],
    metrics: ['Zero No-Shows', '95% Booking Completion', '<30s Scheduling Time'],
    highlights: [
      'Real-time multi-timezone calendar conflict resolution',
      'Automated SMS/WhatsApp reminder sequences with instant reschedule links',
      'Pre-call qualification questionnaire sync to CRM lead profiles',
      'Direct Zoom/Google Meet video link generation with calendar invites'
    ],
    featured: false
  },
  {
    id: 'ai-event-registration-mgmt',
    name: 'AI Event Registration & CRM Contact Automation',
    category: 'Operations & ERP',
    tagline: 'Autonomous Webinar & Event Onboarding, QR Badging & CRM Sync',
    description: 'Intelligent event management pipeline automating landing page creation, attendee ticketing, QR code ticket generation, automated reminder emails, and post-event follow-up nurture flows.',
    image: '/portfolio/ai/ai_event_registration_mgmt_mockup.png',
    stack: ['n8n', 'Stripe Payments', 'SendGrid', 'HubSpot CRM', 'Google Sheets'],
    metrics: ['5,000+ Attendees Handled', '99.9% Delivery Rate', 'Zero Manual Ticketing'],
    highlights: [
      'Dynamic QR code ticket generation and email dispatch upon payment',
      'Automated Zoom webinar registration sync and attendee segment tagging',
      'Automated SMS reminder sequences 24h and 1h before event starts',
      'Post-event recording distribution and survey feedback collection'
    ],
    featured: false
  },
  {
    id: 'market-research-agent',
    name: 'Autonomous Market Research & Competitive Intel Agent',
    category: 'Growth & Leads',
    tagline: 'Automated Competitor Monitoring, Pricing Scraping & PDF Briefings',
    description: 'Self-operating research agent that scans competitor websites, reviews, pricing tiers, and industry news daily, compiling executive summaries with SWOT analyses and pricing shift alerts.',
    image: '/portfolio/ai/market_research_agent_mockup.png',
    stack: ['Puppeteer Web Scraper', 'GPT-4o', 'LangChain', 'Slack Webhook', 'Notion API'],
    metrics: ['Daily Automated Briefs', '50+ Competitors Tracked', '100% Zero Manual Research'],
    highlights: [
      'Headless web scraping with anti-bot evasion and DOM change detection',
      'Automated feature matrix and pricing diff tracking',
      'Weekly executive PDF summary generated and posted to Slack #strategy',
      'Sentiment analysis on competitor ProductHunt, G2, and Trustpilot reviews'
    ],
    featured: false
  },
  {
    id: 'wordpress-blog-automation',
    name: 'Autonomous WordPress & SEO Blog Publishing Engine',
    category: 'Content & Social',
    tagline: 'Automated SEO Keyword Research, Article Writing & CMS Publishing',
    description: 'Automated publishing engine that generates fully-formatted, SEO-optimized articles with featured images, internal link structures, schema markup, and auto-publishes directly to WordPress drafts or live queues.',
    image: '/portfolio/ai/wordpress_blog_automation_mockup.png',
    stack: ['WordPress REST API', 'GPT-4o / Claude', 'DALL-E 3', 'Yoast SEO API', 'Make.com'],
    metrics: ['+260% Organic Search Traffic', 'Rank #1 for 40+ Keywords', '0 Human Editorial Overhead'],
    highlights: [
      'Programmatic keyword optimization and heading structure generation',
      'Automated AI featured image generation and ALT text tagging',
      'Auto-injection of internal linking and FAQ structured schema',
      'Direct publishing to WordPress with category and tag taxonomy'
    ],
    featured: false
  },
  {
    id: 'enterprise-automation-graph',
    name: 'Enterprise Multi-Node Autonomous Pipeline Architecture',
    category: 'Operations & ERP',
    tagline: 'High-Resilience Enterprise Webhook & Microservice Orchestration',
    description: 'Complex visual node architecture powering mission-critical workflows with auto-retry queues, encrypted state persistence, dead-letter monitoring, and bi-directional ERP syncing.',
    image: '/portfolio/ai/workflow_diagram_telemetry_mockup.png',
    stack: ['n8n Enterprise', 'Docker', 'PostgreSQL', 'Redis', 'Webhooks', 'AWS'],
    metrics: ['99.99% Event Delivery', '<100ms Event Trigger', '1M+ Tasks / Month'],
    highlights: [
      'Dead-letter exchange queue with automated self-healing triggers',
      'Encrypted secret credential store and OAuth 2.0 token refreshes',
      'End-to-end execution logs with automated alert webhooks',
      'Sub-second asynchronous parallel branch execution'
    ],
    featured: false
  }
];

const HomePortfolio = ({ isHomePage = false }) => {
  const [activeTab, setActiveTab] = useState('shopify'); // 'shopify' | 'apps' | 'ai'
  const [appFilter, setAppFilter] = useState('All');
  const [shopifyFilter, setShopifyFilter] = useState('All');
  const [aiFilter, setAiFilter] = useState('All');
  const [selectedModalItem, setSelectedModalItem] = useState(null);

  const swiperAppRef = useRef();
  const swiperShopifyRef = useRef();

  // App Filter Categories
  const appCategories = [
    'All',
    'Fintech & SaaS',
    'E-Commerce & Retail',
    'On-Demand & Mobility',
    'Lifestyle & AI',
    'Health & Fitness'
  ];

  // Shopify Filter Categories
  const shopifyCategories = [
    'All',
    'Fashion & Apparel',
    'Beauty & Skincare',
    'Health & Wellness',
    'Health & Tech',
    'Hair Care & Salons',
    'D2C Lifestyle'
  ];

  // AI Filter Categories
  const aiCategories = [
    'All',
    'Voice AI',
    'E-Commerce Ops',
    'Growth & Leads',
    'Operations & ERP',
    'Support & RAG',
    'Content & Social'
  ];

  const filteredApps = appFilter === 'All'
    ? uniqueAppPortfolio
    : uniqueAppPortfolio.filter(app => app.category === appFilter);

  const filteredShopify = shopifyFilter === 'All'
    ? uniqueShopifyPortfolio
    : uniqueShopifyPortfolio.filter(store => store.category === shopifyFilter);

  const filteredAi = aiFilter === 'All'
    ? uniqueAIPortfolio
    : uniqueAIPortfolio.filter(item => item.category === aiFilter);

  return (
    <div className="flex flex-col items-center justify-start max-w-7xl mt-12 sm:mt-16 md:mt-20 mx-auto px-4 w-full">
      <div className="flex flex-col items-center justify-start w-full">
        
        {/* Header Title */}
        <div className="w-[90%] md:w-[70%] m-auto text-center mb-8">
          <span className="text-red-600 font-semibold uppercase tracking-wider text-sm border-b-2 border-red-600 pb-1">
            Proven Engineering & Production Case Studies
          </span>
          {isHomePage ? (
            <h2 className="my-4 font-extrabold text-3xl sm:text-4xl lg:text-5xl text-gray-900 leading-tight">
              Our Work in <span className="text-red-600">Action</span>
            </h2>
          ) : (
            <h1 className="my-4 font-extrabold text-3xl sm:text-4xl lg:text-5xl text-gray-900 leading-tight">
              Our Work in <span className="text-red-600">Action</span>
            </h1>
          )}
          <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
            Explore our deployed high-converting Shopify 2.0 storefronts, Flutter & React Native mobile apps, full-scale SaaS MVPs, and intelligent AI automation workflows.
          </p>
        </div>

        {/* Master Portfolio Segment Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12 p-1.5 bg-gray-100 rounded-2xl border border-gray-200">
          <button
            onClick={() => setActiveTab('shopify')}
            className={`px-5 py-3 rounded-xl font-bold text-sm sm:text-base flex items-center gap-2 transition duration-200 ${
              activeTab === 'shopify'
                ? 'bg-red-600 text-white shadow-lg'
                : 'text-gray-700 hover:text-gray-900 hover:bg-white/60'
            }`}
          >
            <FaShoppingBag /> Shopify Stores ({uniqueShopifyPortfolio.length})
          </button>
          <button
            onClick={() => setActiveTab('apps')}
            className={`px-5 py-3 rounded-xl font-bold text-sm sm:text-base flex items-center gap-2 transition duration-200 ${
              activeTab === 'apps'
                ? 'bg-red-600 text-white shadow-lg'
                : 'text-gray-700 hover:text-gray-900 hover:bg-white/60'
            }`}
          >
            <FaMobileAlt /> Mobile & SaaS Apps ({uniqueAppPortfolio.length})
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`px-5 py-3 rounded-xl font-bold text-sm sm:text-base flex items-center gap-2 transition duration-200 ${
              activeTab === 'ai'
                ? 'bg-red-600 text-white shadow-lg'
                : 'text-gray-700 hover:text-gray-900 hover:bg-white/60'
            }`}
          >
            <FaRobot /> AI Workflows & Voice Agents ({uniqueAIPortfolio.length})
          </button>
        </div>

        {/* =================================================================== */}
        {/* TAB 1: SHOPIFY STORES PORTFOLIO */}
        {/* =================================================================== */}
        {activeTab === 'shopify' && (
          <div className="w-full animate-fadeIn">
            
            {/* Featured Shopify Slider */}
            <div className="w-full mb-14">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <span className="w-2.5 h-6 bg-red-600 rounded-full inline-block"></span>
                    Featured Shopify 2.0 & Headless Storefronts
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    Engineered for high conversion rates, lightning page speeds, and subscription scalability.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    aria-label="Previous Shopify store"
                    onClick={() => swiperShopifyRef.current?.slidePrev()}
                    className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:bg-red-600 hover:text-white hover:border-red-600 transition flex items-center justify-center text-sm font-bold shadow-sm"
                  >
                    &larr;
                  </button>
                  <button
                    aria-label="Next Shopify store"
                    onClick={() => swiperShopifyRef.current?.slideNext()}
                    className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:bg-red-600 hover:text-white hover:border-red-600 transition flex items-center justify-center text-sm font-bold shadow-sm"
                  >
                    &rarr;
                  </button>
                </div>
              </div>

              <Swiper
                modules={[Navigation, Autoplay]}
                autoplay={{ delay: 4500, disableOnInteraction: false }}
                spaceBetween={20}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 1.5 },
                  1024: { slidesPerView: 2.5 },
                }}
                onBeforeInit={(swiper) => {
                  swiperShopifyRef.current = swiper;
                }}
                className="rounded-2xl pb-4"
              >
                {uniqueShopifyPortfolio.map((store) => (
                  <SwiperSlide key={store.id}>
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex flex-col h-full hover:shadow-2xl hover:border-red-200 transition duration-300 group">
                      <div 
                        className="relative overflow-hidden bg-gray-100 aspect-[4/3] cursor-pointer"
                        onClick={() => setSelectedModalItem({ ...store, itemType: 'Shopify Store' })}
                      >
                        <img 
                          src={store.image} 
                          alt={`${store.name} Shopify Storefront`} 
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                          loading="lazy"
                        />
                        <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                          {store.category}
                        </span>
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="bg-white/95 text-gray-900 text-xs font-bold px-4 py-2 rounded-xl shadow-lg flex items-center gap-1.5">
                            <FaStore className="text-red-600" /> View Case Study
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-6 flex flex-col justify-between flex-grow">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <h3 
                              onClick={() => setSelectedModalItem({ ...store, itemType: 'Shopify Store' })}
                              className="text-xl font-bold text-gray-900 group-hover:text-red-600 transition cursor-pointer"
                            >
                              {store.name}
                            </h3>
                            <span className="text-[11px] text-green-600 font-semibold flex items-center gap-1 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                              <FaCheckCircle className="text-[10px]" /> Live Store
                            </span>
                          </div>
                          
                          <p className="text-xs text-red-600 font-semibold mb-2">
                            {store.tagline}
                          </p>
                          <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed mb-4">
                            {store.description}
                          </p>
                        </div>

                        <div>
                          {store.metrics && (
                            <div className="flex flex-wrap gap-1.5 mb-3">
                              {store.metrics.map((m, idx) => (
                                <span key={idx} className="bg-red-50 text-red-700 text-[11px] font-semibold px-2 py-0.5 rounded border border-red-100 flex items-center gap-1">
                                  <FaBolt className="text-[9px]" /> {m}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="flex flex-wrap gap-1.5 mb-4">
                            {store.stack.map((tech, i) => (
                              <span key={i} className="bg-gray-100 text-gray-700 text-[11px] font-medium px-2 py-0.5 rounded">
                                {tech}
                              </span>
                            ))}
                          </div>

                          <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                            <button
                              onClick={() => setSelectedModalItem({ ...store, itemType: 'Shopify Store' })}
                              className="text-xs font-bold text-gray-700 hover:text-red-600 transition"
                            >
                              Case Study & Details &rarr;
                            </button>
                            <Link 
                              to="/Form" 
                              className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1.5"
                            >
                              Build Similar Store <FaArrowRight className="text-[10px]" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Filterable Shopify Store Grid */}
            <div className="w-full mb-16">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
                  All Custom Shopify Storefronts ({uniqueShopifyPortfolio.length})
                </h2>
                
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {shopifyCategories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setShopifyFilter(cat)}
                      className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition ${
                        shopifyFilter === cat
                          ? 'bg-red-600 text-white shadow-md'
                          : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredShopify.map((store) => (
                  <div 
                    key={store.id} 
                    className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div 
                        className="relative overflow-hidden bg-gray-100 aspect-[4/3] cursor-pointer"
                        onClick={() => setSelectedModalItem({ ...store, itemType: 'Shopify Store' })}
                      >
                        <img 
                          src={store.image} 
                          alt={`${store.name} Storefront`} 
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                          loading="lazy"
                        />
                        <span className="absolute top-2.5 right-2.5 bg-black/75 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-0.5 rounded">
                          {store.category}
                        </span>
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="bg-white text-gray-900 text-xs font-bold px-3 py-1.5 rounded-lg shadow">
                            View Case Study
                          </span>
                        </div>
                      </div>

                      <div className="p-4">
                        <h3 
                          onClick={() => setSelectedModalItem({ ...store, itemType: 'Shopify Store' })}
                          className="text-base font-bold text-gray-900 group-hover:text-red-600 transition cursor-pointer"
                        >
                          {store.name}
                        </h3>
                        <p className="text-[11px] text-red-600 font-medium mb-1.5">
                          {store.tagline}
                        </p>
                        <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed mb-3">
                          {store.description}
                        </p>

                        {store.metrics && (
                          <div className="flex flex-wrap gap-1 mb-2.5">
                            {store.metrics.slice(0, 2).map((m, idx) => (
                              <span key={idx} className="bg-red-50 text-red-700 text-[10px] font-semibold px-2 py-0.5 rounded border border-red-100">
                                {m}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-1 mb-2">
                          {store.stack.slice(0, 3).map((item, idx) => (
                            <span key={idx} className="bg-gray-50 border border-gray-200 text-gray-600 text-[9px] font-medium px-1.5 py-0.5 rounded">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="p-4 pt-0 flex gap-2">
                      <button
                        onClick={() => setSelectedModalItem({ ...store, itemType: 'Shopify Store' })}
                        className="flex-1 bg-white hover:bg-gray-50 text-gray-800 text-xs font-bold py-2.5 px-3 rounded-xl border border-gray-200 transition duration-200"
                      >
                        Details
                      </button>
                      <Link
                        to="/Form"
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2.5 px-3 rounded-xl transition duration-200 flex items-center justify-center gap-1"
                      >
                        Get Quote <FaArrowRight className="text-[10px]" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}

        {/* =================================================================== */}
        {/* TAB 2: MOBILE & WEB APPS / SAAS MVPS PORTFOLIO */}
        {/* =================================================================== */}
        {activeTab === 'apps' && (
          <div className="w-full animate-fadeIn">
            
            {/* Featured Apps Slider */}
            <div className="w-full mb-14">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <span className="w-2.5 h-6 bg-red-600 rounded-full inline-block"></span>
                    Featured Mobile Apps & SaaS MVP Platforms
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    Flutter, React Native, high-concurrency SaaS platforms, and enterprise digital products.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    aria-label="Previous featured app"
                    onClick={() => swiperAppRef.current?.slidePrev()}
                    className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:bg-red-600 hover:text-white hover:border-red-600 transition flex items-center justify-center text-sm font-bold shadow-sm"
                  >
                    &larr;
                  </button>
                  <button
                    aria-label="Next featured app"
                    onClick={() => swiperAppRef.current?.slideNext()}
                    className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:bg-red-600 hover:text-white hover:border-red-600 transition flex items-center justify-center text-sm font-bold shadow-sm"
                  >
                    &rarr;
                  </button>
                </div>
              </div>

              <Swiper
                modules={[Navigation, Autoplay]}
                autoplay={{ delay: 4500, disableOnInteraction: false }}
                spaceBetween={20}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 1.5 },
                  1024: { slidesPerView: 2.5 },
                }}
                onBeforeInit={(swiper) => {
                  swiperAppRef.current = swiper;
                }}
                className="rounded-2xl pb-4"
              >
                {uniqueAppPortfolio.map((app) => (
                  <SwiperSlide key={app.id}>
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex flex-col h-full hover:shadow-2xl hover:border-red-200 transition duration-300 group">
                      <div 
                        className="relative overflow-hidden bg-gray-100 aspect-[4/3] cursor-pointer"
                        onClick={() => setSelectedModalItem({ ...app, itemType: 'Mobile & SaaS App' })}
                      >
                        <img 
                          src={app.image} 
                          alt={app.name} 
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                          loading="lazy"
                        />
                        <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                          {app.category}
                        </span>
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="bg-white/95 text-gray-900 text-xs font-bold px-4 py-2 rounded-xl shadow-lg flex items-center gap-1.5">
                            <FaMobileAlt className="text-red-600" /> View Case Study
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-6 flex flex-col justify-between flex-grow">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <h3 
                              onClick={() => setSelectedModalItem({ ...app, itemType: 'Mobile & SaaS App' })}
                              className="text-xl font-bold text-gray-900 group-hover:text-red-600 transition cursor-pointer"
                            >
                              {app.name}
                            </h3>
                            <span className="text-[11px] text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded">
                              Production Ready
                            </span>
                          </div>
                          
                          <p className="text-xs text-red-600 font-semibold mb-2">
                            {app.tagline}
                          </p>
                          <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed mb-4">
                            {app.description}
                          </p>
                        </div>

                        <div>
                          {app.metrics && (
                            <div className="flex flex-wrap gap-1.5 mb-3">
                              {app.metrics.map((m, idx) => (
                                <span key={idx} className="bg-red-50 text-red-700 text-[11px] font-semibold px-2 py-0.5 rounded border border-red-100 flex items-center gap-1">
                                  <FaBolt className="text-[9px]" /> {m}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="flex flex-wrap gap-1.5 mb-4">
                            {app.stack.map((tech, i) => (
                              <span key={i} className="bg-gray-100 text-gray-700 text-[11px] font-medium px-2 py-0.5 rounded">
                                {tech}
                              </span>
                            ))}
                          </div>

                          <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                            <button
                              onClick={() => setSelectedModalItem({ ...app, itemType: 'Mobile & SaaS App' })}
                              className="text-xs font-bold text-gray-700 hover:text-red-600 transition"
                            >
                              Case Study & Details &rarr;
                            </button>
                            <Link 
                              to="/Form" 
                              className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1.5"
                            >
                              Build Similar App <FaArrowRight className="text-[10px]" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Filterable App Grid */}
            <div className="w-full mb-16">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
                  All Mobile Applications & SaaS MVPs ({uniqueAppPortfolio.length})
                </h2>
                
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {appCategories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setAppFilter(cat)}
                      className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition ${
                        appFilter === cat
                          ? 'bg-red-600 text-white shadow-md'
                          : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredApps.map((app) => (
                  <div 
                    key={app.id} 
                    className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div 
                        className="relative overflow-hidden bg-gray-100 aspect-[4/3] cursor-pointer"
                        onClick={() => setSelectedModalItem({ ...app, itemType: 'Mobile & SaaS App' })}
                      >
                        <img 
                          src={app.image} 
                          alt={`${app.name} Showcase`} 
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                          loading="lazy"
                        />
                        <span className="absolute top-3 right-3 bg-black/75 backdrop-blur-sm text-white text-[11px] font-medium px-2.5 py-1 rounded-md">
                          {app.category}
                        </span>
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="bg-white text-gray-900 text-xs font-bold px-3 py-1.5 rounded-lg shadow">
                            View Case Study
                          </span>
                        </div>
                      </div>

                      <div className="p-5">
                        <h3 
                          onClick={() => setSelectedModalItem({ ...app, itemType: 'Mobile & SaaS App' })}
                          className="text-lg font-bold text-gray-900 group-hover:text-red-600 transition cursor-pointer"
                        >
                          {app.name}
                        </h3>
                        <p className="text-xs text-red-600 font-medium mb-2">
                          {app.tagline}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-4">
                          {app.description}
                        </p>

                        {app.metrics && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {app.metrics.map((m, idx) => (
                              <span key={idx} className="bg-red-50 text-red-700 text-[10px] font-semibold px-2 py-0.5 rounded border border-red-100">
                                {m}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {app.stack.map((item, idx) => (
                            <span key={idx} className="bg-gray-50 border border-gray-200 text-gray-600 text-[10px] font-medium px-2 py-0.5 rounded">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="p-5 pt-0 flex gap-2">
                      <button
                        onClick={() => setSelectedModalItem({ ...app, itemType: 'Mobile & SaaS App' })}
                        className="flex-1 bg-white hover:bg-gray-50 text-gray-800 text-xs font-bold py-2.5 px-3 rounded-xl border border-gray-200 transition duration-200"
                      >
                        Details
                      </button>
                      <Link
                        to="/Form"
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2.5 px-3 rounded-xl transition duration-200 flex items-center justify-center gap-1"
                      >
                        Get Quote <FaArrowRight className="text-[10px]" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}

        {/* =================================================================== */}
        {/* TAB 3: AI WORKFLOWS, VOICE AGENTS & AUTOMATION SUITE (SECONDARY) */}
        {/* =================================================================== */}
        {activeTab === 'ai' && (
          <div className="w-full animate-fadeIn mb-16">
            
            {/* AI Banner */}
            <div className="text-center mb-10">
              <span className="text-xs font-bold text-red-600 uppercase tracking-wider bg-red-50 px-3 py-1 rounded-full border border-red-100">
                Secondary Acceleration Suite
              </span>
              <h2 className="mt-3 font-extrabold text-2xl sm:text-3xl lg:text-4xl text-gray-900">
                AI Workflow Automations & Voice Calling Telephony
              </h2>
              <p className="text-gray-600 text-sm sm:text-base max-w-3xl mx-auto mt-2">
                Operational acceleration systems: Human-grade 24/7 AI voice calling agents, multi-agent n8n/Make workflows, invoice OCR parsing, and custom RAG knowledge assistants.
              </p>

              {/* AI Filter Pills */}
              <div className="flex flex-wrap justify-center gap-2 mt-6">
                {aiCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setAiFilter(cat)}
                    className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition ${
                      aiFilter === cat
                        ? 'bg-red-600 text-white shadow-md'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Featured AI Voice Calling Agent Showcase Card */}
            {(aiFilter === 'All' || aiFilter === 'Voice AI') && (
              <div className="mb-12 bg-gradient-to-br from-gray-950 via-slate-900 to-black text-white rounded-3xl p-6 sm:p-10 shadow-2xl border border-gray-800 flex flex-col lg:flex-row items-center gap-8 group">
                <div 
                  className="w-full lg:w-1/2 relative rounded-2xl overflow-hidden cursor-pointer aspect-video bg-black/50 border border-gray-700 shadow-inner"
                  onClick={() => setSelectedModalItem({ ...uniqueAIPortfolio[0], itemType: 'AI Voice Calling System' })}
                >
                  <img 
                    src="/portfolio/ai/ai_voice_calling_agent_showcase.jpg" 
                    alt="AI Voice Calling Agent Dashboard Mockup" 
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 bg-red-600 text-white text-[11px] font-bold px-3 py-1 rounded-full shadow flex items-center gap-1.5">
                    <FaMicrophoneAlt /> 24/7 Live Telephony Agent
                  </div>
                  <div className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-md text-cyan-300 text-[11px] font-mono px-3 py-1 rounded-md border border-cyan-500/30">
                    Latency: &lt;420ms | 99.4% CSAT
                  </div>
                </div>

                <div className="w-full lg:w-1/2 flex flex-col justify-between">
                  <div>
                    <span className="text-xs font-bold text-red-500 uppercase tracking-wider">
                      Featured Voice Telephony Showcase
                    </span>
                    <h3 className="text-2xl sm:text-3xl font-extrabold text-white mt-1 mb-2">
                      Enterprise 24/7 Conversational AI Voice Calling Agent
                    </h3>
                    <p className="text-xs text-red-400 font-semibold mb-3">
                      Sub-500ms Real-Time Inbound Support, Outbound Sales & Dynamic Calendar Booking
                    </p>
                    <p className="text-sm text-gray-300 leading-relaxed mb-4">
                      Ultra-low latency conversational telephony agent engineered with Retell AI, ElevenLabs Turbo, and Twilio. Handles customer inquiries with natural interruptions, adapts emotional tone, streams live transcriptions, and auto-syncs booking details to HubSpot, Salesforce, and Shopify.
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
                      <div className="bg-white/5 border border-white/10 p-2.5 rounded-xl text-center">
                        <div className="text-xs text-gray-300 font-medium">Latency</div>
                        <div className="text-sm font-bold text-cyan-400">&lt;420ms</div>
                      </div>
                      <div className="bg-white/5 border border-white/10 p-2.5 rounded-xl text-center">
                        <div className="text-xs text-gray-300 font-medium">Booking Rate</div>
                        <div className="text-sm font-bold text-green-400">99.4%</div>
                      </div>
                      <div className="bg-white/5 border border-white/10 p-2.5 rounded-xl text-center">
                        <div className="text-xs text-gray-300 font-medium">Calls Handled</div>
                        <div className="text-sm font-bold text-white">10,000+</div>
                      </div>
                      <div className="bg-white/5 border border-white/10 p-2.5 rounded-xl text-center">
                        <div className="text-xs text-gray-300 font-medium">CSAT Rating</div>
                        <div className="text-sm font-bold text-yellow-400">98%</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {['Retell AI', 'ElevenLabs Turbo', 'Twilio Voice', 'Whisper STT', 'HubSpot API', 'FastAPI'].map((t, i) => (
                        <span key={i} className="bg-white/10 text-gray-200 text-xs px-2.5 py-1 rounded-lg border border-white/10">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setSelectedModalItem({ ...uniqueAIPortfolio[0], itemType: 'AI Voice Calling System' })}
                      className="bg-white hover:bg-gray-100 text-gray-900 font-bold text-xs sm:text-sm px-5 py-3 rounded-xl transition shadow"
                    >
                      Inspect Architecture & Details &rarr;
                    </button>
                    <Link
                      to="/Form"
                      className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs sm:text-sm px-5 py-3 rounded-xl transition shadow flex items-center gap-1.5"
                    >
                      Deploy Voice Agent <FaArrowRight className="text-xs" />
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* AI Automation Workflows Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredAi.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl transition duration-300 flex flex-col justify-between group"
                >
                  <div>
                    <div 
                      className="relative overflow-hidden bg-gray-900 aspect-[16/10] cursor-pointer"
                      onClick={() => setSelectedModalItem({ ...item, itemType: 'AI Automation Workflow' })}
                    >
                      <img 
                        src={item.image} 
                        alt={item.name} 
                        className="w-full h-full object-contain p-2 group-hover:scale-105 transition duration-500"
                        loading="lazy"
                      />
                      <span className="absolute top-3 left-3 bg-red-600 text-white text-[11px] font-bold px-3 py-0.5 rounded-full shadow">
                        {item.category}
                      </span>
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="bg-white text-gray-900 text-xs font-bold px-3 py-1.5 rounded-lg shadow flex items-center gap-1.5">
                          <FaRobot className="text-red-600" /> View Workflow Diagram
                        </span>
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 
                        onClick={() => setSelectedModalItem({ ...item, itemType: 'AI Automation Workflow' })}
                        className="text-lg font-bold text-gray-900 group-hover:text-red-600 transition cursor-pointer"
                      >
                        {item.name}
                      </h3>
                      <p className="text-xs text-red-600 font-semibold mb-2">
                        {item.tagline}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-4">
                        {item.description}
                      </p>

                      {item.metrics && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {item.metrics.map((m, idx) => (
                            <span key={idx} className="bg-red-50 text-red-700 text-[10px] font-semibold px-2 py-0.5 rounded border border-red-100 flex items-center gap-1">
                              <FaBolt className="text-[9px]" /> {m}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {item.stack.map((t, idx) => (
                          <span key={idx} className="bg-gray-100 text-gray-700 text-[10px] font-medium px-2 py-0.5 rounded">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="p-5 pt-0 flex gap-2">
                    <button
                      onClick={() => setSelectedModalItem({ ...item, itemType: 'AI Automation Workflow' })}
                      className="flex-1 bg-white hover:bg-gray-50 text-gray-800 text-xs font-bold py-2.5 px-3 rounded-xl border border-gray-200 transition duration-200"
                    >
                      Details & Flow
                    </button>
                    <Link
                      to="/Form"
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2.5 px-3 rounded-xl transition duration-200 flex items-center justify-center gap-1"
                    >
                      Build Workflow <FaArrowRight className="text-[10px]" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}

        {/* Bottom CTA Card */}
        <div className="w-full bg-gradient-to-r from-gray-900 to-black text-white rounded-3xl p-8 sm:p-12 text-center shadow-2xl flex flex-col items-center mt-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-4">
            Have a Shopify Store, Mobile App, or SaaS Platform in Mind?
          </h2>
          <p className="text-gray-300 max-w-2xl text-sm sm:text-base mb-8 leading-relaxed">
            Let's turn your vision into a scalable, high-performance Shopify storefront, cross-platform Flutter mobile app, or multi-tenant SaaS platform—accelerated by intelligent AI automation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link 
              to="/Form" 
              className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3.5 rounded-xl shadow-lg transition text-sm sm:text-base"
            >
              Get a Free Project Estimate
            </Link>
            <Link 
              to="/Contact" 
              className="bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-3.5 rounded-xl border border-white/20 transition text-sm sm:text-base"
            >
              Talk to an Architect
            </Link>
          </div>
        </div>

      </div>

      {/* =================================================================== */}
      {/* INTERACTIVE CASE STUDY / WORKFLOW INSPECTOR MODAL */}
      {/* =================================================================== */}
      {selectedModalItem && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm animate-fadeIn"
          onClick={() => setSelectedModalItem(null)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl border border-gray-100 max-w-4xl w-full max-h-[92vh] overflow-y-auto overflow-x-hidden p-6 sm:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedModalItem(null)}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-gray-100 hover:bg-red-600 hover:text-white transition flex items-center justify-center text-gray-600 z-10 shadow"
              aria-label="Close modal"
            >
              <FaTimes className="text-sm" />
            </button>

            {/* Modal Image Display */}
            <div className="relative rounded-2xl overflow-hidden bg-gray-950 aspect-[16/10] mb-6 shadow-md border border-gray-200 flex items-center justify-center">
              <img 
                src={selectedModalItem.image} 
                alt={selectedModalItem.name} 
                className="w-full h-full object-contain"
              />
              <span className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold px-3.5 py-1.5 rounded-full shadow-lg">
                {selectedModalItem.category}
              </span>
            </div>

            {/* Modal Content */}
            <div className="flex flex-col gap-4">
              <div>
                <span className="text-xs font-bold text-red-600 uppercase tracking-wider">
                  {selectedModalItem.itemType || 'Case Study Architecture'}
                </span>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mt-1">
                  {selectedModalItem.name}
                </h3>
                <p className="text-sm font-semibold text-gray-600 mt-1">
                  {selectedModalItem.tagline}
                </p>
              </div>

              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {selectedModalItem.description}
              </p>

              {/* Performance & Impact Metrics */}
              {selectedModalItem.metrics && selectedModalItem.metrics.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 my-2">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FaChartLine className="text-red-600" /> Measured Operational Impact
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {selectedModalItem.metrics.map((m, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-xl border border-gray-200 text-center font-bold text-gray-900 text-xs sm:text-sm shadow-sm">
                        {m}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Features / Highlights Delivered */}
              {selectedModalItem.highlights && (
                <div className="my-2">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2.5">
                    Delivered Capabilities & Architecture
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-gray-700">
                    {selectedModalItem.highlights.map((h, idx) => (
                      <li key={idx} className="flex items-start gap-2 bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                        <FaCheckCircle className="text-green-600 text-xs mt-0.5 flex-shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tech Stack */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Technologies & Node Integrations
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedModalItem.stack.map((tech, idx) => (
                    <span key={idx} className="bg-gray-100 border border-gray-200 text-gray-800 text-xs font-semibold px-3 py-1 rounded-lg">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Modal CTA Footer */}
              <div className="pt-6 border-t border-gray-100 flex flex-col sm:flex-row gap-3 mt-4">
                <Link
                  to="/Form"
                  onClick={() => setSelectedModalItem(null)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3.5 px-6 rounded-xl text-center shadow-lg transition flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  Request Similar Project <FaArrowRight />
                </Link>
                <Link
                  to="/Contact"
                  onClick={() => setSelectedModalItem(null)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-3.5 px-6 rounded-xl text-center transition text-sm sm:text-base"
                >
                  Talk to Our Team
                </Link>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default HomePortfolio;